import { useState } from "react";
import {
  Activity,
  CheckCircle,
  ClipboardList,
  Clock,
  DollarSign,
  FileText,
  Home,
  Hourglass,
  LayoutDashboard,
  Minus,
  Search,
  TrendingUp,
  Upload,
  Wallet,
  XCircle,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { DashboardPageHeader } from "@/shared/ui/DashboardPageHeader";
import { DashboardSectionLabel } from "@/shared/ui/DashboardSectionLabel";
import { AuditorInvoiceDistributionChart } from "../components/auditor-dashboard/AuditorInvoiceDistributionChart";
import { AuditorProductivityIndicators } from "../components/auditor-dashboard/AuditorProductivityIndicators";
import { AuditorDashboardInvoicesSection } from "../components/auditor-dashboard/AuditorDashboardInvoicesSection";
import { DashboardMetricCard } from "../components/dashboard/DashboardMetricCard";
import {
  DashboardQuickAccess,
  type DashboardQuickAccessConfig,
} from "../components/dashboard/DashboardQuickAccess";
import { useAuditorDashboard } from "../hooks/useAuditorDashboard";
import { getRoleStatRoute } from "../navigation/dashboardRoutes";

const indicatorIcons = {
  file: FileText,
  upload: Upload,
  wallet: Wallet,
  clock: Clock,
  check: CheckCircle,
  x: XCircle,
  clipboard: ClipboardList,
};

const footerIcons = {
  minus: Minus,
  hourglass: Hourglass,
  dollar: DollarSign,
  activity: Activity,
  trending: TrendingUp,
  x: XCircle,
};

const auditorMetricConfig = {
  invoices: {
    cardClassName:
      "!border-primary/45 !bg-white hover:!border-primary/65 dark:!bg-white",
    iconClassName: "rounded-2xl bg-primary text-white shadow-sm",
    valueClassName: "text-primary",
    titleClassName: "font-semibold text-primary",
  },
  submitted: {
    cardClassName:
      "!border-yellow-400/50 !bg-white hover:!border-yellow-400/65 dark:!border-yellow-500/40 dark:!bg-white",
    iconClassName: "rounded-2xl bg-yellow-400/30 text-amber-800 shadow-sm dark:text-primary",
    titleClassName: "font-semibold text-foreground",
  },
  review: {
    cardClassName:
      "!border-yellow-400/50 !bg-white hover:!border-yellow-400/65 dark:!border-yellow-500/40 dark:!bg-white",
    iconClassName: "rounded-2xl bg-yellow-400/30 text-amber-800 shadow-sm dark:text-primary",
    titleClassName: "font-semibold text-foreground",
  },
  approved: {
    cardClassName:
      "!border-green/45 !bg-white hover:!border-green/55 dark:!bg-white",
    iconClassName: "rounded-2xl bg-green/25 text-primary shadow-sm",
    valueClassName: "text-primary",
    titleClassName: "font-semibold text-primary",
  },
  paid: {
    cardClassName:
      "!border-green/45 !bg-white hover:!border-green/55 dark:!bg-white",
    iconClassName: "rounded-2xl bg-green/25 text-primary shadow-sm",
    valueClassName: "text-primary",
    titleClassName: "font-semibold text-primary",
  },
  rejected: {
    cardClassName:
      "!border-red-500/40 !bg-white hover:!border-red-500/55 dark:!bg-white",
    iconClassName: "rounded-2xl bg-red-500/15 text-red-600 shadow-sm dark:text-red-400",
    valueClassName: "text-red-600 dark:text-red-400",
    titleClassName: "font-semibold",
  },
  validation: {
    cardClassName:
      "!border-primary/45 !bg-white hover:!border-primary/65 dark:!bg-white",
    iconClassName: "rounded-2xl bg-primary text-white shadow-sm",
    valueClassName: "text-primary",
    titleClassName: "font-semibold text-primary",
  },
} as const;

const auditorQuickAccessConfig: Record<string, DashboardQuickAccessConfig> = {
  toAudit: {
    icon: Search,
    cardClassName:
      "!border-primary/45 !bg-white hover:!border-primary/65 dark:!bg-white",
    iconClassName: "bg-primary text-white shadow-sm",
    labelClassName: "font-semibold text-primary",
  },
  approved: {
    icon: CheckCircle,
    cardClassName:
      "!border-green/45 !bg-white hover:!border-green/55 dark:!bg-white",
    iconClassName: "rounded-2xl bg-green/25 text-primary shadow-sm",
    labelClassName: "font-semibold text-primary",
  },
  rejected: {
    icon: XCircle,
    cardClassName:
      "!border-red-500/40 !bg-white hover:!border-red-500/55 dark:!bg-white",
    iconClassName: "rounded-2xl bg-red-500/15 text-red-600 shadow-sm dark:text-red-400",
    labelClassName: "font-semibold",
  },
  admissions: {
    icon: Home,
    cardClassName:
      "!border-green !bg-green shadow-[0_8px_24px_rgba(184,228,23,0.22)] hover:!brightness-[0.97]",
    iconClassName: "bg-white/45 text-primary shadow-sm",
    labelClassName: "font-semibold text-primary",
  },
};

export function AuditorDashboardPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.dashboard.auditorBoard;
  const { data, loading, error, usingApi } = useAuditorDashboard();
  const [activeQuickAccess, setActiveQuickAccess] = useState("toAudit");

  const subtitle = labels.subtitle
    .replace("{invoices}", String(data.header.invoicesToAudit))
    .replace("{requests}", String(data.header.pendingRequests));

  const quickAccessItems = data.quickActions.map((action) => ({
    id: action.id,
    href: action.href,
    label: labels.quickActions[action.id as keyof typeof labels.quickActions],
  }));

  return (
    <div className="space-y-5">
      <DashboardPageHeader
        title={labels.pageTitle}
        subtitle={subtitle}
        icon={LayoutDashboard}
        badge={data.header.roleBadge}
        date={data.header.displayDate}
      />

      {loading && <p className="text-sm text-muted">Chargement…</p>}
      {error && <p className="text-sm text-danger">{error}</p>}
      {usingApi && !loading && (
        <p className="text-xs text-muted">Données synchronisées depuis l&apos;API.</p>
      )}

      <div>
        <DashboardSectionLabel>{labels.sections.indicators}</DashboardSectionLabel>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {data.indicators.map((indicator) => {
            const Icon = indicatorIcons[indicator.icon];
            const FooterIcon = footerIcons[indicator.footerIcon];
            const label = labels.indicators[indicator.id as keyof typeof labels.indicators];
            const footer =
              labels.indicatorFooters[indicator.footerKey as keyof typeof labels.indicatorFooters];
            const config = auditorMetricConfig[indicator.id as keyof typeof auditorMetricConfig];

            return (
              <DashboardMetricCard
                key={indicator.id}
                title={label}
                value={indicator.value}
                footer={
                  <span className="inline-flex items-center gap-1">
                    <FooterIcon size={12} />
                    {footer}
                  </span>
                }
                icon={Icon}
                href={getRoleStatRoute("auditor", indicator.id)}
                footerClassName="justify-end"
                {...config}
              />
            );
          })}
        </div>
      </div>

      <div>
        <DashboardSectionLabel>{labels.sections.quickAccess}</DashboardSectionLabel>
        <DashboardQuickAccess
          items={quickAccessItems}
          configById={auditorQuickAccessConfig}
          activeId={activeQuickAccess}
          onSelect={setActiveQuickAccess}
        />
      </div>

      <div>
        <DashboardSectionLabel>{labels.sections.charts}</DashboardSectionLabel>
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <AuditorInvoiceDistributionChart />
          <AuditorProductivityIndicators />
        </div>
      </div>

      <div>
        <DashboardSectionLabel>{labels.sections.invoices}</DashboardSectionLabel>
        <AuditorDashboardInvoicesSection invoices={data.submittedInvoices} />
      </div>
    </div>
  );
}
